import { ValidationResult, ValidationRule, ValidatorOptions } from './types';

/**
 * Base validator class providing common validation functionality
 * All specific validators should extend this class
 */
export abstract class BaseValidator<T = unknown> {
  protected rules: ValidationRule<T>[] = [];
  protected options: ValidatorOptions;

  constructor(options: ValidatorOptions = {}) {
    this.options = { required: false, ...options };
  }

  /**
   * Add a validation rule
   */
  addRule(rule: ValidationRule<T>): this {
    this.rules.push(rule);
    return this;
  }

  /**
   * Add multiple validation rules
   */
  addRules(rules: ValidationRule<T>[]): this {
    this.rules.push(...rules);
    return this;
  }

  /**
   * Validate a value against all rules
   */
  validate(value: T, context?: Record<string, unknown>): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required field first
    if (this.options.required && this.isEmpty(value)) {
      errors.push(this.options.customMessage || 'This field is required');
      return { isValid: false, errors, warnings };
    }

    // Skip validation if not required and empty
    if (!this.options.required && this.isEmpty(value)) {
      return { isValid: true, errors: [], warnings };
    }

    // Run all validation rules
    for (const rule of this.rules) {
      const result = rule.validate(value, context);
      if (!result.isValid) {
        errors.push(...result.errors);
      }
      if (result.warnings) {
        warnings.push(...result.warnings);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Check if a value is considered empty
   */
  protected isEmpty(value: T): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  /**
   * Create a copy of this validator with new options
   */
  withOptions(options: Partial<ValidatorOptions>): this {
    const newValidator = Object.create(Object.getPrototypeOf(this));
    newValidator.rules = [...this.rules];
    newValidator.options = { ...this.options, ...options };
    return newValidator;
  }

  /**
   * Mark this validator as required
   */
  required(message?: string): this {
    return this.withOptions({
      required: true,
      customMessage: message
    });
  }

  /**
   * Set a custom error message
   */
  withMessage(message: string): this {
    return this.withOptions({ customMessage: message });
  }
}